<ion-grid>
  <!-- Header with Stats -->
  <ion-row>
    <ion-col>
      <ion-card>
        <ion-card-header>
          <ion-card-title>Knowledge Gap Insights</ion-card-title>
          <ion-card-subtitle
            >AI-detected insights and actionable recommendations for your
            knowledge base</ion-card-subtitle
          >
        </ion-card-header>
      </ion-card>
    </ion-col>
  </ion-row>

  <!-- Filters and Search -->
  <ion-row>
    <ion-col>
      <div class="filter-controls">
        <ion-searchbar
          mode="ios"
          placeholder="Search insights..."
          showCancelButton="never"
          class="rounded-search-bar"
          [(ngModel)]="searchText"
          (ionChange)="onSearch()"
        >
        </ion-searchbar>

        <app-filter-button
          [options]="filterOptions"
          (didChange)="filtersDidChange($event)"
        >
        </app-filter-button>
      </div>
    </ion-col>
  </ion-row>

  <!-- Insights List -->
  <ion-row>
    <ion-col>
      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-container">
        <ion-spinner></ion-spinner>
        <p>Loading insights...</p>
      </div>

      <!-- Insights List -->
      <div
        *ngIf="!isLoading && insightsDataSource.lastResponse?.data?.length > 0"
      >
        <ion-card
          *ngFor="
            let insight of insightsDataSource.lastResponse.data;
            trackBy: trackByInsight
          "
          class="insight-card"
        >
          <ion-card-content>
            <div class="category-header">
              <h2>{{ insight.category }}</h2>
            </div>

            <p class="synopsis">{{ getSynopsis(insight) }}</p>

            <div class="metadata">
              <span class="conversation-count">
                {{ getTotalConversationCount(insight) }} conversations
              </span>
              <span class="date">
                {{ displayDate(insight.createdAt) }}
              </span>
            </div>

            <!-- Topics List -->
            <div
              style="
                margin-top: 1rem;
                padding-top: 1rem;
                border-top: 1px solid var(--ion-color-light);
              "
            >
              <ion-item
                *ngFor="let topic of insight.topics"
                lines="none"
                style="--padding-start: 0"
              >
                <ion-label>
                  <h3 style="margin: 0; font-size: 0.9rem">
                    <a
                      [href]="getConversationUrlByTopic(topic)"
                      target="_blank"
                      style="
                        color: var(--ion-color-primary);
                        text-decoration: none;
                      "
                    >
                      {{ getConversationTitle(topic) }}
                    </a>
                  </h3>
                  <p
                    style="
                      margin: 0.25rem 0 0 0;
                      font-size: 0.8rem;
                      color: var(--ion-color-medium);
                    "
                  >
                    {{ topic.conversation.summary }}
                  </p>
                </ion-label>
                <div
                  *ngIf="getActionForTopic(insight, topic)"
                  slot="end"
                  style="display: flex; align-items: center; gap: 0.25rem"
                  [style.opacity]="
                    getActionForTopic(insight, topic).isDismissed ? '0.6' : '1'
                  "
                >
                  <ion-button
                    *ngIf="!getActionForTopic(insight, topic).isDismissed"
                    fill="clear"
                    size="small"
                    color="primary"
                    (click)="
                      executeTopicAction(
                        topic,
                        getActionForTopic(insight, topic)
                      )
                    "
                  >
                    {{ getActionButtonText(getActionForTopic(insight, topic)) }}
                    <ion-icon name="create-outline" slot="end"></ion-icon>
                  </ion-button>
                  <ion-button
                    *ngIf="getActionForTopic(insight, topic).isDismissed"
                    fill="clear"
                    size="small"
                    color="secondary"
                    (click)="
                      undismissTopicAction(
                        insight,
                        topic,
                        getActionForTopic(insight, topic)
                      )
                    "
                  >
                    <ion-icon name="refresh" slot="start"></ion-icon>
                    Restore Action
                  </ion-button>
                  <ion-button
                    *ngIf="!getActionForTopic(insight, topic).isDismissed"
                    fill="clear"
                    size="small"
                    color="medium"
                    (click)="
                      dismissTopicAction(
                        insight,
                        topic,
                        getActionForTopic(insight, topic)
                      )
                    "
                  >
                    <ion-icon name="close" slot="icon-only"></ion-icon>
                  </ion-button>
                  <ion-chip
                    *ngIf="getActionForTopic(insight, topic).isDismissed"
                    color="medium"
                    size="small"
                  >
                    Dismissed
                  </ion-chip>
                </div>
              </ion-item>
            </div>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Empty State -->
      <div
        *ngIf="
          !isLoading &&
          (!insightsDataSource.lastResponse ||
            insightsDataSource.lastResponse.data.length === 0)
        "
        class="empty-state"
      >
        <ion-card>
          <ion-card-content class="ion-text-center">
            <ion-icon
              name="bulb-outline"
              size="large"
              color="medium"
            ></ion-icon>
            <h3>No Knowledge Gap Insights</h3>
            <p>No insights found. This could mean:</p>
            <ul>
              <li>Your knowledge base is comprehensive</li>
              <li>No patterns have been detected yet</li>
              <li>Try adjusting your filters</li>
            </ul>
          </ion-card-content>
        </ion-card>
      </div>

      <!-- Pagination Controls -->
      <div
        *ngIf="!isLoading && insightsDataSource.lastResponse?.data?.length > 0"
        class="pagination-container"
      >
        <ion-button
          fill="clear"
          [disabled]="
            insightsDataSource.lastResponse?.cursors?.hasPrevious !== true
          "
          (click)="previousPage()"
        >
          <ion-icon name="chevron-back" slot="start"></ion-icon>
          Previous
        </ion-button>
        <span class="pagination-info">
          {{ insightsDataSource.lastResponse?.data?.length || 0 }}
          insights
          <span *ngIf="insightsDataSource.lastResponse?.totalCount">
            of {{ insightsDataSource.lastResponse.totalCount }}
          </span>
        </span>
        <ion-button
          fill="clear"
          [disabled]="
            insightsDataSource.lastResponse?.cursors?.hasNext !== true
          "
          (click)="nextPage()"
        >
          Next
          <ion-icon name="chevron-forward" slot="end"></ion-icon>
        </ion-button>
      </div>
    </ion-col>
  </ion-row>
</ion-grid>
